/**
 * Capture the cross-app prompt handoff. See `./CLAUDE.md` for the contract.
 *
 * Runs once at app boot from `RootLayout` (`src/routes/__root.tsx`). Reads the
 * URL query param `?juice_prompt=…` (preferred — fresh, no roundtrip) and falls
 * back to the `juice_prompt` cookie set by the sender on `.juicelabs.ai` (the
 * survivor when an OAuth redirect strips the query). Stashes the result in
 * sessionStorage so it survives the login round-trip, then scrubs the URL and
 * clears the cookie so a refresh doesn't replay.
 *
 * Idempotent — short-circuits if sessionStorage already holds a captured
 * prompt, so a remount or refresh doesn't re-read a stale cookie.
 */

import {
  HANDOFF_COOKIE_DOMAIN,
  HANDOFF_MAX_LENGTH,
  HANDOFF_QUERY_KEY,
  HANDOFF_STORAGE_KEY,
} from './constants'

export function capturePendingPrompt(): void {
  if (typeof window === 'undefined') return
  if (sessionStorage.getItem(HANDOFF_STORAGE_KEY)) return

  const raw = readFromUrl() ?? readFromCookie()
  if (!raw) return

  const prompt = raw.slice(0, HANDOFF_MAX_LENGTH).trim()
  if (!prompt) return

  sessionStorage.setItem(HANDOFF_STORAGE_KEY, prompt)
  scrubUrl()
  clearCookie()

  if (import.meta.env.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[handoff] captured', prompt.length, 'chars')
  }
}

/**
 * URL channel — `URLSearchParams.get()` decodes once. DO NOT decode again,
 * or prompts containing literal `%` will throw `URIError` and be dropped.
 */
function readFromUrl(): string | null {
  const value = new URLSearchParams(window.location.search).get(HANDOFF_QUERY_KEY)
  return value && value.length > 0 ? value : null
}

/**
 * Cookie channel — `document.cookie` returns the raw encoded form. Decode
 * exactly once. A malformed sequence (truncated UTF-8) throws — drop silently.
 */
function readFromCookie(): string | null {
  const prefix = `${HANDOFF_QUERY_KEY}=`
  const parts = document.cookie.split('; ')
  for (const part of parts) {
    if (part.startsWith(prefix)) {
      const encoded = part.slice(prefix.length)
      try {
        return decodeURIComponent(encoded)
      } catch {
        return null
      }
    }
  }
  return null
}

/**
 * Remove only the handoff param — preserve UTM params, OAuth return state,
 * and anything else the sender or the auth flow may have appended.
 */
function scrubUrl(): void {
  const url = new URL(window.location.href)
  if (!url.searchParams.has(HANDOFF_QUERY_KEY)) return
  url.searchParams.delete(HANDOFF_QUERY_KEY)
  window.history.replaceState(null, '', url.pathname + url.search + url.hash)
}

/**
 * Clear the parent-domain cookie. Throws on hosts the parent domain doesn't
 * cover (e.g. localhost during dev) — that's fine, the URL channel is the
 * working path there.
 */
function clearCookie(): void {
  try {
    document.cookie = `${HANDOFF_QUERY_KEY}=; domain=${HANDOFF_COOKIE_DOMAIN}; path=/; max-age=0`
  } catch {
    // Silent — see comment above.
  }
}
